import React, { useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, BookIcon, CheckCircleIcon, ChevronRightIcon, SearchIcon, PlusCircleIcon, XIcon, GlobeIcon, MapPinIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import BackButton from '../../../components/UI/BackButton';
import { bookstores as directoryStores } from '../../../data/bookstoresData';
import { allBooks } from '../../../data/booksData';
interface CommunityStore {
  id: string;
  name: string;
  type: string;
  city: string;
  country: string;
  website: string;
  specializations: string[];
  logo: string;
  slug: string;
  isCommunityAdded: true;
}
const LinkBooksToStores = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Mock vendor books
  const vendorBookIds = ['1', '2', '3'];
  const vendorBooks = allBooks.filter(b => vendorBookIds.includes(b.id));
  const [activeTab, setActiveTab] = useState<'by-book' | 'by-store'>('by-book');
  const [step, setStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  // Selection state
  const [selectedBooks, setSelectedBooks] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [availabilityType, setAvailabilityType] = useState('stocked');
  // Search state
  const [bookSearch, setBookSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  // Community-added stores
  const [communityStores, setCommunityStores] = useState<CommunityStore[]>([]);
  const [showAddStore, setShowAddStore] = useState(false);
  const [newStore, setNewStore] = useState({
    name: '',
    type: 'brick-and-mortar',
    city: '',
    country: '',
    website: ''
  });
  // All stores = directory + community-added
  const allStores = useMemo(() => [...directoryStores, ...communityStores], [communityStores]);
  // Filtered books
  const filteredBooks = useMemo(() => {
    if (!bookSearch.trim()) return vendorBooks;
    const q = bookSearch.toLowerCase();
    return vendorBooks.filter(b => b.title.toLowerCase().includes(q) || b.author.toLowerCase().includes(q) || b.genres.some(g => g.toLowerCase().includes(q)) || b.seriesName && b.seriesName.toLowerCase().includes(q));
  }, [bookSearch, vendorBooks]);
  // Filtered stores
  const filteredStores = useMemo(() => {
    if (!storeSearch.trim()) return allStores;
    const q = storeSearch.toLowerCase();
    return allStores.filter(s => s.name.toLowerCase().includes(q) || s.city && s.city.toLowerCase().includes(q) || s.country.toLowerCase().includes(q) || s.specializations?.some(sp => sp.toLowerCase().includes(q)));
  }, [storeSearch, allStores]);
  const handleBookToggle = (id: string) => {
    setSelectedBooks(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };
  const handleStoreToggle = (id: string) => {
    setSelectedStores(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleSubmit = () => {
    setIsSuccess(true);
  };
  const resetFlow = () => {
    setStep(1);
    setSelectedBooks([]);
    setSelectedStores([]);
    setBookSearch('');
    setStoreSearch('');
    setIsSuccess(false);
  };
  const handleAddStore = () => {
    if (!newStore.name.trim()) return;
    const id = `community-${Date.now()}`;
    const slug = newStore.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const store: CommunityStore = {
      id,
      name: newStore.name,
      type: newStore.type,
      city: newStore.city,
      country: newStore.country || 'Unknown',
      website: newStore.website,
      specializations: [],
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newStore.name)}&background=8b5cf6&color=fff&size=128`,
      slug,
      isCommunityAdded: true
    };
    setCommunityStores(prev => [...prev, store]);
    setSelectedStores(prev => [...prev, id]);
    setNewStore({
      name: '',
      type: 'brick-and-mortar',
      city: '',
      country: '',
      website: ''
    });
    setShowAddStore(false);
    setStoreSearch('');
  };
  // Success screen
  if (isSuccess) {
    return <div className={`min-h-screen py-16 flex items-center justify-center ${dk.page}`} data-id="element-18720" data-mp="2opssrgiqjkud">
        <div className={`max-w-md w-full p-8 rounded-2xl shadow-lg border text-center ${dk.card}`} data-id="element-18721" data-mp="2tkf4jksqbfez">
          <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-18722" data-mp="2jsj4i4731f84">
            <CheckCircleIcon className="h-8 w-8" data-id="element-18723" data-mp="25srmlsdg3eou" />
          </div>
          <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-18724" data-mp="2as4fouuwthzv">
            Successfully Linked!
          </h2>
          <p className={`mb-8 ${dk.textSecondary}`} data-id="element-18725" data-mp="1r5svjyii2man">
            You have successfully linked {selectedBooks.length} book(s) to{' '}
            {selectedStores.length} store(s). The listings are now active.
          </p>
          <div className="flex flex-col gap-3" data-id="element-18726" data-mp="208278rpi8lpm">
            <Link to="/vendors/store-listings" className={`w-full py-3 rounded-lg font-medium transition-colors ${rt.primaryButton}`} data-id="element-18727" data-mp="204o4hicxiahn">
              View Store Listings
            </Link>
            <button onClick={resetFlow} className={`w-full py-3 rounded-lg font-medium transition-colors border ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`} data-id="element-18728" data-mp="256lpah0z9dcp">
              Link More Books
            </button>
          </div>
        </div>
      </div>;
  }
  // Determine if we're showing the book selection step or store selection step
  const isBookStep = activeTab === 'by-book' && step === 1 || activeTab === 'by-store' && step === 2;
  const isStoreStep = activeTab === 'by-book' && step === 2 || activeTab === 'by-store' && step === 1;
  return <div className={`min-h-screen pt-20 pb-24 md:py-8 ${dk.page}`} data-id="element-18729" data-mp="1uk3piuf37bnj">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18730" data-mp="1uhr427a4ej5q">
        <div className="mb-6" data-id="element-18731" data-mp="1zbdhi1o3yr2l">
          <BackButton data-id="element-18732" data-mp="1zcokhz9qbusn" />
        </div>

        <div className="mb-8" data-id="element-18733" data-mp="24a6kv19ol1p1">
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-18734" data-mp="248vin5vvw201">
            Link Books to Stores
          </h1>
          <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-18735" data-mp="296wyzj1xdn7t">
            Connect your catalog to retail partners to show readers where they
            can buy your books.
          </p>
        </div>

        {/* Flow Toggle */}
        <div className={`p-1 flex rounded-lg mb-8 max-w-md mx-auto ${dk.isDark ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-18736" data-mp="2i73grh4np9cs">
          <button onClick={() => {
          setActiveTab('by-book');
          resetFlow();
        }} className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'by-book' ? `bg-white dark:bg-gray-700 shadow-sm ${dk.textPrimary}` : dk.textMuted}`} data-id="element-18737" data-mp="nun6844tv924">
            Start with Books
          </button>
          <button onClick={() => {
          setActiveTab('by-store');
          resetFlow();
        }} className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'by-store' ? `bg-white dark:bg-gray-700 shadow-sm ${dk.textPrimary}` : dk.textMuted}`} data-id="element-18738" data-mp="2khinp356vpj">
            Start with Stores
          </button>
        </div>

        {/* Stepper UI */}
        <div className={`rounded-2xl border shadow-sm overflow-hidden ${dk.card}`} data-id="element-18739" data-mp="lbidc0x8c53a">
          {/* Progress Header */}
          <div className={`px-6 py-4 border-b flex items-center justify-between ${dk.isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50'}`} data-id="element-18740" data-mp="3b12leo56ksk7">
            <div className="flex items-center gap-2" data-id="element-18741" data-mp="19x08on1rl24g">
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${step >= 1 ? rt.badgeBg + ' text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-18742" data-mp="1dzokn3lryp5n">
                1
              </span>
              <span className={`text-sm font-medium hidden sm:block ${step >= 1 ? dk.textPrimary : dk.textMuted}`} data-id="element-18743" data-mp="14zrq4lcpew2o">
                {activeTab === 'by-book' ? 'Select Books' : 'Select Store'}
              </span>
              <ChevronRightIcon className={`h-4 w-4 mx-1 sm:mx-2 ${dk.textMuted}`} data-id="element-18744" data-mp="1xpgt348z566x" />

              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${step >= 2 ? rt.badgeBg + ' text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-18745" data-mp="1nxkw5wimjm4a">
                2
              </span>
              <span className={`text-sm font-medium hidden sm:block ${step >= 2 ? dk.textPrimary : dk.textMuted}`} data-id="element-18746" data-mp="2gfq493mqo8ar">
                {activeTab === 'by-book' ? 'Select Stores' : 'Select Books'}
              </span>
              <ChevronRightIcon className={`h-4 w-4 mx-1 sm:mx-2 ${dk.textMuted}`} data-id="element-18747" data-mp="dr8eaftgq8xg" />

              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${step >= 3 ? rt.badgeBg + ' text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-18748" data-mp="9pd4so1dop8l">
                3
              </span>
              <span className={`text-sm font-medium hidden sm:block ${step >= 3 ? dk.textPrimary : dk.textMuted}`} data-id="element-18749" data-mp="3d8g2w8wfmt12">
                Details
              </span>
            </div>
            <span className={`text-sm font-medium ${dk.textMuted}`} data-id="element-18750" data-mp="3m9ebl4htfopz">
              Step {step} of 3
            </span>
          </div>

          <div className="p-6" data-id="element-18751" data-mp="11hkibfll7lir">
            {/* BOOK SELECTION STEP */}
            {isBookStep && <div data-id="element-18752" data-mp="271lqlrjjurlj">
                <h2 className={`text-xl font-bold mb-4 ${dk.textPrimary}`} data-id="element-18753" data-mp="26zr0zzxc2bb4">
                  Select Books to Link
                </h2>

                {/* Book Search */}
                <div className="relative mb-4" data-id="element-18754" data-mp="1xaqyw7njazsp">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-18755" data-mp="1x9fymhtasa6c" />
                  <input type="text" placeholder="Search by title, author, genre, or series..." value={bookSearch} onChange={e => setBookSearch(e.target.value)} className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18756" data-mp="1ob3z1qage21r" />
                  {bookSearch && <button onClick={() => setBookSearch('')} className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded ${dk.isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`} data-id="element-18757" data-mp="38pw69oym1min">
                      <XIcon className={`h-3.5 w-3.5 ${dk.textMuted}`} data-id="element-18758" data-mp="vfmlsdbi4xgi" />
                    </button>}
                </div>

                {filteredBooks.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18759" data-mp="14883rr7b704a">
                    {filteredBooks.map(book => <label key={book.id} className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${selectedBooks.includes(book.id) ? `${rt.borderActive} ${dk.isDark ? 'bg-indigo-900/20' : 'bg-indigo-50'}` : `${dk.isDark ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'}`}`} data-id="element-18760" data-mp="7j8ty7r2tq9z">
                        <input type="checkbox" checked={selectedBooks.includes(book.id)} onChange={() => handleBookToggle(book.id)} className={`h-5 w-5 rounded border-gray-300 mr-4 ${rt.checkbox}`} data-id="element-18761" data-mp="1rx938oy5z0xf" />
                        <img src={book.coverImage} alt="" className="w-10 h-14 object-cover rounded shadow-sm mr-3" data-id="element-18762" data-mp="3p0rexbdvz3lr" />
                        <div data-id="element-18763" data-mp="3a9s9uac9ex6x">
                          <p className={`font-bold text-sm ${dk.textPrimary}`} data-id="element-18764" data-mp="3a90qbxvxspx0">
                            {book.title}
                          </p>
                          <p className={`text-xs ${dk.textMuted}`} data-id="element-18765" data-mp="358mh4ciln9xe">
                            {book.formats.join(', ')}
                          </p>
                        </div>
                      </label>)}
                  </div> : <div className={`text-center py-10 rounded-xl border border-dashed ${dk.isDark ? 'border-gray-700' : 'border-gray-300'}`} data-id="element-18766" data-mp="2rhq25ohlir8t">
                    <BookIcon className={`h-8 w-8 mx-auto mb-3 ${dk.textMuted}`} data-id="element-18767" data-mp="121461caieako" />
                    <p className={`text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18768" data-mp="16u6rex54udps">
                      No books match "{bookSearch}"
                    </p>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-18769" data-mp="x3c3zpg5h82r">
                      Try a different search term or clear the filter.
                    </p>
                  </div>}

                {selectedBooks.length > 0 && <div className={`mt-4 pt-4 border-t flex items-center justify-between ${dk.borderSubtle}`} data-id="element-18770" data-mp="1bsi1n8zcaq8j">
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-18771" data-mp="1gp8ihyiyxpv0">
                      <span className="font-bold" data-id="element-18772" data-mp="bs1o7lieund">{selectedBooks.length}</span>{' '}
                      book(s) selected
                    </span>
                    <button onClick={() => setSelectedBooks([])} className={`text-xs ${dk.textMuted} hover:underline`} data-id="element-18773" data-mp="59jvj75psqu7">
                      Clear selection
                    </button>
                  </div>}
              </div>}

            {/* STORE SELECTION STEP */}
            {isStoreStep && <div data-id="element-18774" data-mp="6sbr44vedynp">
                <h2 className={`text-xl font-bold mb-4 ${dk.textPrimary}`} data-id="element-18775" data-mp="13k5in839ef7q">
                  {activeTab === 'by-store' ? 'Select a Store' : 'Select Stores'}
                </h2>

                {/* Store Search */}
                <div className="relative mb-4" data-id="element-18776" data-mp="uhwb8r3af1yk">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-18777" data-mp="pl5wc737yceq" />
                  <input type="text" placeholder="Search by store name, city, or specialization..." value={storeSearch} onChange={e => setStoreSearch(e.target.value)} className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18778" data-mp="3fb9k04dclc6i" />
                  {storeSearch && <button onClick={() => setStoreSearch('')} className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded ${dk.isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`} data-id="element-18779" data-mp="23kr5u6y3y320">
                      <XIcon className={`h-3.5 w-3.5 ${dk.textMuted}`} data-id="element-18780" data-mp="2dgkirrfqnykb" />
                    </button>}
                </div>

                {filteredStores.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18781" data-mp="2miairlnos5tp">
                    {filteredStores.map(store => {
                const isCommunity = 'isCommunityAdded' in store && (store as CommunityStore).isCommunityAdded;
                return <label key={store.id} className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${selectedStores.includes(store.id) ? `${rt.borderActive} ${dk.isDark ? 'bg-indigo-900/20' : 'bg-indigo-50'}` : `${dk.isDark ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'}`}`} data-id="element-18782" data-mp="1vht16v56zs5h">
                          <input type={activeTab === 'by-store' ? 'radio' : 'checkbox'} name="store-selection" checked={selectedStores.includes(store.id)} onChange={() => {
                    if (activeTab === 'by-store') {
                      setSelectedStores([store.id]);
                    } else {
                      handleStoreToggle(store.id);
                    }
                  }} className={`h-5 w-5 border-gray-300 mr-4 ${activeTab === 'by-store' ? 'rounded-full' : 'rounded'} ${rt.checkbox}`} data-id="element-18783" data-mp="29g99urmm7sq9" />
                          <img src={store.logo} alt="" className="w-10 h-10 object-cover rounded-lg mr-3" data-id="element-18784" data-mp="36sejm8ictcq9" />
                          <div className="flex-1 min-w-0" data-id="element-18785" data-mp="2xtb053f9yvb8">
                            <div className="flex items-center gap-2" data-id="element-18786" data-mp="2j3d98fpynric">
                              <p className={`font-bold text-sm truncate ${dk.textPrimary}`} data-id="element-18787" data-mp="2j56fhtwnw9pt">
                                {store.name}
                              </p>
                              {isCommunity && <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" data-id="element-18788" data-mp="2f18h5lf5v2xt">
                                  New
                                </span>}
                            </div>
                            <p className={`text-xs ${dk.textMuted}`} data-id="element-18789" data-mp="2kktzvaagplj5">
                              {store.city ? `${store.city}, ` : ''}
                              {store.country}
                            </p>
                          </div>
                        </label>;
              })}
                  </div> : <div className={`text-center py-10 rounded-xl border border-dashed ${dk.isDark ? 'border-gray-700' : 'border-gray-300'}`} data-id="element-18790" data-mp="21v3vpwkt7nek">
                    <StoreIcon className={`h-8 w-8 mx-auto mb-3 ${dk.textMuted}`} data-id="element-18791" data-mp="1wty2zyr5g051" />
                    <p className={`text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-18792" data-mp="1nvl0u5h0e8k7">
                      No stores match "{storeSearch}"
                    </p>
                    <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-18793" data-mp="8c50vfxe8935">
                      Can't find the store you're looking for? Add it to the
                      directory.
                    </p>
                    <button onClick={() => {
                setNewStore(prev => ({
                  ...prev,
                  name: storeSearch
                }));
                setShowAddStore(true);
              }} className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${rt.primaryButton}`} data-id="element-18794" data-mp="hdl8jfwtpx3o">
                      <PlusCircleIcon className="h-4 w-4 mr-2" data-id="element-18795" data-mp="3ulobh0t29qi9" />
                      Add "{storeSearch}" as a New Store
                    </button>
                  </div>}

                {/* Persistent "Add a Store" link (always visible) */}
                {!showAddStore && <button onClick={() => setShowAddStore(true)} className={`mt-4 flex items-center text-sm font-medium transition-colors ${rt.accentLink}`} data-id="element-18796" data-mp="29jxamue28f3z">
                    <PlusCircleIcon className="h-4 w-4 mr-1.5" data-id="element-18797" data-mp="2igpp9i0azo0g" />
                    Don't see your store? Add it to the directory
                  </button>}

                {/* Add Store Inline Form */}
                {showAddStore && <div className={`mt-4 p-5 rounded-xl border ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-18798" data-mp="1utpyewenbsbq">
                    <div className="flex items-center justify-between mb-4" data-id="element-18799" data-mp="24jt49x6v90rh">
                      <h3 className={`font-bold text-base ${dk.textPrimary}`} data-id="element-18800" data-mp="173nex4osruoq">
                        Add a New Store
                      </h3>
                      <button onClick={() => setShowAddStore(false)} className={`p-1 rounded ${dk.isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`} data-id="element-18801" data-mp="1c0vx359y3ypz">
                        <XIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-18802" data-mp="zi0a31hp8hrl" />
                      </button>
                    </div>
                    <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-18803" data-mp="plx57nvgq50y">
                      Add the basics so other vendors can find and link to this
                      store too. The store owner can claim and complete the
                      profile later.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18804" data-mp="bua0czngfo6k">
                      <div className="sm:col-span-2" data-id="element-18805" data-mp="bs5y0m57ex8e">
                        <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-18806" data-mp="6vys2tct5lqg">
                          Store Name <span className="text-red-500" data-id="element-18807" data-mp="3tzvdee9g42uq">*</span>
                        </label>
                        <input type="text" value={newStore.name} onChange={e => setNewStore(prev => ({
                    ...prev,
                    name: e.target.value
                  }))} placeholder="e.g. The Velvet Bookmark" className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18808" data-mp="3u298gn44lf2a" />
                      </div>

                      <div data-id="element-18809" data-mp="3tdh7vobsvkdq">
                        <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-18810" data-mp="3tc64hpm9oen5">
                          Store Type
                        </label>
                        <select value={newStore.type} onChange={e => setNewStore(prev => ({
                    ...prev,
                    type: e.target.value
                  }))} className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18811" data-mp="punubbj0uvjk">
                          <option value="brick-and-mortar" data-id="element-18812" data-mp="18t0x3lxhpt3u">
                            Brick & Mortar
                          </option>
                          <option value="online-only" data-id="element-18813" data-mp="h1b8t71j0as3">Online Only</option>
                          <option value="hybrid" data-id="element-18814" data-mp="h34g8ojyqz15">
                            Hybrid (Physical + Online)
                          </option>
                          <option value="pop-up" data-id="element-18815" data-mp="q29dlrjsgmbq">Pop-up</option>
                          <option value="specialty" data-id="element-18816" data-mp="q42mldhvjimw">Specialty</option>
                        </select>
                      </div>

                      <div data-id="element-18817" data-mp="21heubzgr8lec">
                        <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-18818" data-mp="21g3gt7xibza1">
                          Country <span className="text-red-500" data-id="element-18819" data-mp="fz4cr21x5fko">*</span>
                        </label>
                        <input type="text" value={newStore.country} onChange={e => setNewStore(prev => ({
                    ...prev,
                    country: e.target.value
                  }))} placeholder="e.g. United States" className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18820" data-mp="g0ywid1gf1n6" />
                      </div>

                      <div data-id="element-18821" data-mp="1cpwvk7g8llq5">
                        <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-18822" data-mp="1cqpsyhtb2yvm">
                          City
                        </label>
                        <input type="text" value={newStore.city} onChange={e => setNewStore(prev => ({
                    ...prev,
                    city: e.target.value
                  }))} placeholder="e.g. Portland" className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18823" data-mp="3283ipzafj37e" />
                      </div>

                      <div data-id="element-18824" data-mp="3c2vhh6fqzw3r">
                        <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-18825" data-mp="375mu8rfqt1vn">
                          Website (Optional)
                        </label>
                        <input type="url" value={newStore.website} onChange={e => setNewStore(prev => ({
                    ...prev,
                    website: e.target.value
                  }))} placeholder="https://thevelvetbookmark.com" className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18826" data-mp="3omt6ak9drhe" />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-18827" data-mp="8rnp5f7ar84e">
                      <button onClick={() => setShowAddStore(false)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-white text-gray-700'}`} data-id="element-18828" data-mp="3v0f3dn1jmns6">
                        Cancel
                      </button>
                      <button onClick={handleAddStore} disabled={!newStore.name.trim() || !newStore.country.trim()} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${rt.primaryButton}`} data-id="element-18829" data-mp="39qaq6vxeay6e">
                        <span className="flex items-center" data-id="element-18830" data-mp="3sh1zjxhduoks">
                          <PlusCircleIcon className="h-4 w-4 mr-1.5" data-id="element-18831" data-mp="3simx7vyzunmv" />
                          Add Store & Select
                        </span>
                      </button>
                    </div>
                  </div>}

                {selectedStores.length > 0 && <div className={`mt-4 pt-4 border-t flex items-center justify-between ${dk.borderSubtle}`} data-id="element-18832" data-mp="2c4d5qszggirt">
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-18833" data-mp="1m8b691qkrct6">
                      <span className="font-bold" data-id="element-18834" data-mp="1q93ebrnpnbnv">{selectedStores.length}</span>{' '}
                      store(s) selected
                    </span>
                    <button onClick={() => setSelectedStores([])} className={`text-xs ${dk.textMuted} hover:underline`} data-id="element-18835" data-mp="1v14n33lo6k95">
                      Clear selection
                    </button>
                  </div>}
              </div>}

            {/* STEP 3 CONTENT */}
            {step === 3 && <div data-id="element-18836" data-mp="1c8am5dkvssnx">
                <h2 className={`text-xl font-bold mb-6 ${dk.textPrimary}`} data-id="element-18837" data-mp="1c80qpah2htau">
                  Availability Details
                </h2>

                <div className={`p-5 rounded-xl border mb-6 ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-18838" data-mp="2noh8hag3gm0v">
                  <p className={`text-sm mb-2 ${dk.textSecondary}`} data-id="element-18839" data-mp="2spohfctz3r94">
                    You are linking{' '}
                    <span className="font-bold" data-id="element-18840" data-mp="h22v4jcirfzd">
                      {selectedBooks.length} book(s)
                    </span>{' '}
                    to{' '}
                    <span className="font-bold" data-id="element-18841" data-mp="78ufdak8rc8h">
                      {selectedStores.length} store(s)
                    </span>
                    .
                  </p>

                  {/* Show selected items summary */}
                  <div className="mt-3 space-y-2" data-id="element-18842" data-mp="3ucs4u1ntpkv4">
                    <div data-id="element-18843" data-mp="3pf0dp9v062rz">
                      <span className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-18844" data-mp="3peqm5hub7nk6">
                        Books:
                      </span>
                      <div className="flex flex-wrap gap-1.5 mt-1" data-id="element-18845" data-mp="3fjyqijkafas1">
                        {selectedBooks.map(id => {
                      const book = vendorBooks.find(b => b.id === id);
                      return book ? <span key={id} className={`text-xs px-2 py-0.5 rounded-md ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`} data-id="element-18846" data-mp="25geuf8doaz4d">
                              {book.title}
                            </span> : null;
                    })}
                      </div>
                    </div>
                    <div data-id="element-18847" data-mp="1mikioml5u9ia">
                      <span className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-18848" data-mp="1mjvnmpqcdnaz">
                        Stores:
                      </span>
                      <div className="flex flex-wrap gap-1.5 mt-1" data-id="element-18849" data-mp="1rhf2nruv643z">
                        {selectedStores.map(id => {
                      const store = allStores.find(s => s.id === id);
                      return store ? <span key={id} className={`text-xs px-2 py-0.5 rounded-md ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`} data-id="element-18850" data-mp="32vivuvtmno7z">
                              {store.name}
                            </span> : null;
                    })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 max-w-md" data-id="element-18851" data-mp="3t7chqvxizfeo">
                  <label className="block" data-id="element-18852" data-mp="3t8nnv2eg0z8y">
                    <span className={`block text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-18853" data-mp="1yyms4o8s1dc">
                      Availability Type
                    </span>
                    <select value={availabilityType} onChange={e => setAvailabilityType(e.target.value)} className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18854" data-mp="3oejfcepj4j5r">
                      <option value="stocked" data-id="element-18855" data-mp="ulgmcsfwu74d">
                        In Stock (Physical Copies)
                      </option>
                      <option value="signed-copy" data-id="element-18856" data-mp="qlo75ww0h2q1">
                        Signed Copies Available
                      </option>
                      <option value="pre-order" data-id="element-18857" data-mp="gmp8n74ymngr">Available for Pre-order</option>
                      <option value="online-listing" data-id="element-18858" data-mp="gotaljjatcee">
                        Online Listing Only
                      </option>
                    </select>
                  </label>

                  <label className="block" data-id="element-18859" data-mp="6vm949roigjl">
                    <span className={`block text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-18860" data-mp="2v2af1rgj4vc">
                      Direct Link (Optional)
                    </span>
                    <input type="url" placeholder="https://store.com/your-book" className={`w-full px-4 py-3 rounded-lg border focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18861" data-mp="3p31y4d6yrczp" />
                    <p className={`text-xs mt-1.5 ${dk.textMuted}`} data-id="element-18862" data-mp="3a1v26whoj0ah">
                      If the store has a specific URL for your book, add it
                      here.
                    </p>
                  </label>
                </div>
              </div>}
          </div>

          {/* Footer Actions */}
          <div className={`px-6 py-4 border-t flex items-center justify-between ${dk.isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`} data-id="element-18863" data-mp="2r998oiu8vlt2">
            {step > 1 ? <button onClick={handleBack} className={`px-4 py-2 rounded-md font-medium text-sm transition-colors border ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-white text-gray-700'}`} data-id="element-18864" data-mp="316x65gj8j7ex">
                Back
              </button> : <div data-id="element-18865" data-mp="2dfi1vi7weg11"></div>}

            {step < 3 ? <button onClick={handleNext} disabled={step === 1 && activeTab === 'by-book' && selectedBooks.length === 0 || step === 1 && activeTab === 'by-store' && selectedStores.length === 0 || step === 2 && activeTab === 'by-book' && selectedStores.length === 0 || step === 2 && activeTab === 'by-store' && selectedBooks.length === 0} className={`px-6 py-2 rounded-md font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${rt.primaryButton}`} data-id="element-18866" data-mp="j25tyuw1hpxa">
                Next Step
              </button> : <button onClick={handleSubmit} className={`px-6 py-2 rounded-md font-medium text-sm transition-colors ${rt.primaryButton}`} data-id="element-18867" data-mp="38iv3s7maxjpu">
                Confirm & Link
              </button>}
          </div>
        </div>
      </div>
    </div>;
};
export default LinkBooksToStores;